import React from 'react';
import { useHistory } from 'react-router-dom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Colors } from 'library';
import { Card, Icon, Modal, PillButton, Row, Stack, Text } from 'library/ui';
import UserAttributesApi from 'shared/UserAttributesApi';
import { useUserContext } from 'UserContext';

import type { SubPoolSummary } from '../types';
import { FadeSlideIn, HeaderSection } from './elcStyles';

function formatCountyList(counties: string[]): string {
    if (counties.length === 0) return '';
    if (counties.length === 1) return counties[0];
    if (counties.length === 2) return `${counties[0]} or ${counties[1]}`;
    return `${counties.slice(0, -1).join(', ')}, or ${counties[counties.length - 1]}`;
}

export function ELCEligibilityModal({
    partner,
    open,
    onClose,
}: {
    partner: SubPoolSummary;
    open: boolean;
    onClose: () => void;
}) {
    const history = useHistory();
    const { user, refreshAlreadyLoggedInUser } = useUserContext();
    const countyList = formatCountyList(partner.county_names);
    const attributeId = user?.sub_pool_welcome_attribute?.id;

    function closeModal() {
        if (attributeId) {
            UserAttributesApi.remove(attributeId).then(refreshAlreadyLoggedInUser);
        }
        onClose();
    }

    function handleGoToManageCenters() {
        closeModal();
        history.push('/manage-centers');
    }

    if (!open) {
        return null;
    }

    return (
        <Modal onClose={closeModal} width={480} padding="none">
            <HeaderSection gap="sm" align="center" padding="lg">
                <FadeSlideIn delay={150}>
                    <Icon icon={AutoAwesomeIcon} size="md" bgColor={Colors.secondary} iconColor={Colors.white} />
                </FadeSlideIn>
                <FadeSlideIn delay={250}>
                    <Text size="lg" weight="bold" tone="overlay" align="center">
                        ELC partnership required for fee-free access
                    </Text>
                </FadeSlideIn>
            </HeaderSection>
            <Stack gap="md" padding="md">
                <FadeSlideIn delay={300}>
                    <Card tone="muted" padding="md">
                        <Stack gap="sm">
                            <Text size="lg" weight="bold">
                                We identified that your center is in {countyList} county, but isn&apos;t contracted with
                                the ELC
                            </Text>
                            <Text tone="secondary">
                                You can still book substitutes through Tandem, but ELC-contracted providers get access
                                with no additional fees. If you are an ELC-contracted provider, add or update your
                                licensed number to see if you qualify.
                            </Text>
                        </Stack>
                    </Card>
                </FadeSlideIn>
                <FadeSlideIn delay={450}>
                    <Row justify="center" fullWidth>
                        <PillButton onClick={handleGoToManageCenters}>Go to Manage Centers</PillButton>
                    </Row>
                </FadeSlideIn>
            </Stack>
        </Modal>
    );
}
